import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Upload, Search } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTaxYear } from '@/contexts/TaxYearContext';
import { getUserDocuments, deleteDocument, downloadDocument } from '@/services/documentApi';
import { Document } from '@/types/document';
import { DocumentList } from '@/components/documents/DocumentList';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function DocumentsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedYear } = useTaxYear();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadDocuments();
  }, [user, selectedYear]);

  useEffect(() => {
    filterDocuments();
  }, [documents, searchQuery]);

  const loadDocuments = async () => {
    if (!user) return;

    try {
      const docs = await getUserDocuments(user.id, selectedYear);
      setDocuments(docs);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  };

  const filterDocuments = () => {
    let filtered = [...documents];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (doc) =>
          doc.name.toLowerCase().includes(query) ||
          doc.originalName.toLowerCase().includes(query)
      );
    }

    setFilteredDocuments(filtered);
  };

  const handleView = (doc: Document) => {
    toast.info(`Viewing: ${doc.originalName}`);
    // In a real app, this would open a preview modal or new tab
  };

  const handleDownload = async (doc: Document) => {
    try {
      const blob = await downloadDocument(doc.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.originalName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Downloaded: ${doc.originalName}`);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDeleteClick = (doc: Document) => {
    setDocumentToDelete(doc);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    setIsDeleting(true);
    try {
      await deleteDocument(documentToDelete.id);
      setDocuments((prev) => prev.filter((d) => d.id !== documentToDelete.id));
      toast.success(`Deleted: ${documentToDelete.originalName}`);
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    } catch (error) {
      toast.error('Failed to delete document');
    } finally {
      setIsDeleting(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
  };

  const hasActiveFilters = !!searchQuery;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">My Documents</h1>
          <p className="text-muted-foreground">
            View and manage your uploaded tax documents for the selected year
          </p>
        </div>
        <Button onClick={() => navigate('/upload')}>
          <Upload className="mr-2 h-4 w-4" />
          Upload Documents
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-2">
          {hasActiveFilters && (
            <Button variant="ghost" onClick={clearFilters}>
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Document List */}
      <DocumentList
        documents={filteredDocuments}
        isLoading={isLoading}
        onView={handleView}
        onDownload={handleDownload}
        onDelete={handleDeleteClick}
        emptyTitle="No documents found"
        emptyDescription={
          hasActiveFilters
            ? 'No documents match your current filters.'
            : 'You haven\'t uploaded any documents yet. Start by uploading your tax documents.'
        }
        emptyAction={
          hasActiveFilters
            ? { label: 'Clear Filters', onClick: clearFilters }
            : { label: 'Upload Documents', onClick: () => navigate('/upload') }
        }
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Document"
        description={`Are you sure you want to delete "${documentToDelete?.originalName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        variant="destructive"
        loading={isDeleting}
      />
    </div>
  );
}
